import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Card, Space, Switch, Tabs, Tag, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTabHistory } from '../utils/useTabHistory';
import { errorMessage } from '../api/client';
import { useInvestor, useUpdateInvestorStatus } from '../api/hooks';
import DealBookSwitch from '../components/common/DealBookSwitch';
import FixedInvestmentsTab from '../components/common/FixedInvestmentsTab';
import FixedProfitTab from '../components/common/FixedProfitTab';
import FixedLedgerTab from '../components/common/FixedLedgerTab';
import FixedReceiptsTab from '../components/common/FixedReceiptsTab';
import FixedWithdrawalsTab from '../components/common/FixedWithdrawalsTab';
import InvestmentsTab from '../components/investor/InvestmentsTab';
import LedgerTab from '../components/investor/LedgerTab';
import ProfileTab from '../components/investor/ProfileTab';
import ProfitTab from '../components/investor/ProfitTab';
import ReceiptsTab from '../components/investor/ReceiptsTab';
import WithdrawalsTab from '../components/investor/WithdrawalsTab';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import type { DealType } from '../types';

export default function InvestorDetailPage() {
  const { t } = useTranslation();
  const tabs = useTabHistory('profile');
  const { id } = useParams<{ id: string }>();
  const investorId = Number(id);
  const navigate = useNavigate();
  const { hasRole, canWrite } = useAuth();
  const { data } = useInvestor(investorId);
  const updateStatus = useUpdateInvestorStatus();
  // An investor may hold both books at once. One selection is shared by every tab, so switching
  // in Investments leaves Ledger and Receipts showing the same kind of record.
  const [book, setBook] = useState<DealType>('PercentageBased');
  const openingBook = data?.dealType;
  useEffect(() => {
    if (openingBook) setBook(openingBook);
  }, [openingBook]);

  const toggleStatus = async (active: boolean) => {
    try {
      await updateStatus.mutateAsync({ id: investorId, status: active ? 'Active' : 'Inactive' });
      message.success(`Investor ${active ? 'activated' : 'deactivated'}.`);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 16, flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            {t('common.back')}
          </Button>
          <Typography.Title level={4} style={{ margin: 0 }}>
            <span data-no-translate>{data ? `${data.investorCode} — ${data.fullName}` : ''}</span>
          </Typography.Title>
          {data && <Tag color={data.status === 'Active' ? 'green' : 'default'}>{data.status}</Tag>}
        </Space>

        <Space>
          {hasRole('Admin') && data && (
            <>
              <span>{t('common.active')}</span>
              <Switch checked={data.status === 'Active'} loading={updateStatus.isPending} onChange={toggleStatus} />
            </>
          )}
        </Space>
      </Space>

      <Card>
        <Tabs
          activeKey={tabs.activeKey}
          onChange={tabs.onChange}
          items={[
            { key: 'profile', label: t('tabs.profile'), children: <ProfileTab investorId={investorId} /> },
            {
              key: 'investments',
              label: t('tabs.investments'),
              children: (
                <DealBookSwitch
                  book={book}
                  onChange={setBook}
                  percentage={<InvestmentsTab investorId={investorId} />}
                  fixed={<FixedInvestmentsTab partyType="Investor" partyId={investorId} canWrite={canWrite} />}
                />
              ),
            },
            {
              key: 'profit',
              label: t('tabs.profit'),
              children: (
                <DealBookSwitch
                  book={book}
                  onChange={setBook}
                  percentage={<ProfitTab investorId={investorId} />}
                  fixed={<FixedProfitTab partyType="Investor" partyId={investorId} />}
                />
              ),
            },
            {
              key: 'withdrawals',
              label: t('tabs.withdrawals'),
              children: (
                <DealBookSwitch
                  book={book}
                  onChange={setBook}
                  percentage={<WithdrawalsTab investorId={investorId} />}
                  fixed={<FixedWithdrawalsTab partyType="Investor" partyId={investorId} canWrite={canWrite} />}
                />
              ),
            },
            {
              key: 'ledger',
              label: t('tabs.ledger'),
              children: (
                <DealBookSwitch
                  book={book}
                  onChange={setBook}
                  percentage={<LedgerTab investorId={investorId} />}
                  fixed={<FixedLedgerTab partyType="Investor" partyId={investorId} />}
                />
              ),
            },
            {
              key: 'receipts',
              label: t('tabs.receipts'),
              children: (
                <DealBookSwitch
                  book={book}
                  onChange={setBook}
                  percentage={<ReceiptsTab investorId={investorId} />}
                  fixed={<FixedReceiptsTab partyType="Investor" partyId={investorId} />}
                />
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}
